import {
  Home,
  BookOpen,
  Info,
  PlusCircle,
  PlusSquare,
  User,
  Users,
  File,
  PieChart,
  SparkleHeart,
  Archive,
  DollarSign,
  CheckCircle,
  Lock,
  Settings,
  Box,
  Database,
  Login,
  Lightbulb,
  List,
  Mail,
  Helix,
  Package,
  CheckList,
  Briefcase,
  NodeMesh,
  Procedure,
  CapsulePills,
  Library,
  Cpu,
} from '@lifeomic/chromicons';
// Will backfill this icon
// eslint-disable-next-line no-restricted-imports
import idx from 'idx';
import {
  Circles,
  Sliders,
  Subjects,
} from '@lifeomic/phc-web-toolkit/src/components/Icons';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { MarketplaceIcon } from '../components/common/icons';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { lifeAccount } from '../redux/util/constants';

import urls from '../util/urls';
import { enforce, strictEnforce, permissions } from '../util/abac';
import _ from 'lodash';
import pwtUrls from '@lifeomic/phc-web-toolkit/src/util/urls';
import getIsInFedEnvironment from '@lifeomic/phc-web-toolkit/src/util/getIsInFedEnvironment';
import { getIsAccountFeatureDisabled } from '@lifeomic/phc-web-toolkit/src/hooks/useIsAccountFeatureDisabled';
import { AbacReducedPolicy } from '@lifeomic/abac';

export const testIds = {
  home: 'tile-home',
  accountInformation: 'tile-accountInformation',
  manageProjects: 'tile-manageProjects',
  invitations: 'tile-invitations',
  manageCohorts: 'tile-manageCohorts',
  manageKnowledge: 'tile-manageKnowledge',
  manageProjectKnowledge: 'tile-manageProjectKnowledge',
  billing: 'tile-billing',
  manageMetadata: 'tile-manageMetadata',
  usage: 'tile-usage',
  apiKeys: 'tile-apiKeys',
  authClients: 'tile-authClients',
  manageAccounts: 'tile-manageAccounts',
  createAccount: 'tile-createAccount',
  accountAuditing: 'tile-accountAuditing',
  manageAccountUsers: 'tile-manageAccountUsers',
  groups: 'tile-groups',
  accessControl: 'tile-accessControl',
  projects: 'tile-projects',
  addData: 'tile-addData',
  subjects: 'tile-subjects',
  subjectSearch: 'tile-subjectSearch',
  files: 'tile-files',
  sftp: 'tile-sftp',
  fileTransfer: 'tile-filesTransfer',
  fileActions: 'tile-fileActions',
  addFileAction: 'tile-addFileAction',
  insights: 'tile-insights',
  genomics: 'tile-genomics',
  genomicsTests: 'tile-genomicsTests',
  genomicsAddData: 'tile-genomicsAddData',
  genomicsIngestions: 'tile-genomicsIngestion',
  genomicsActions: 'tile-genomicsActions',
  tasks: 'tile-tasks',
  automation: 'tile-automation',
  automationAddWorkflow: 'tile-automationAddWorkflow',
  automationTasks: 'tile-automationTasks',
  automationToolsRegistry: 'tile-automationToolsRegistry',
  educationContent: 'tile-educationContent',
  manageEHRs: 'tile-manageEHRs',
  features: 'tile-features',
  manageProjectConsent: 'tile-manageProjectConsent',
  consent: 'tile-consent',
  userGuide: 'tile-userGuide',
  customApps: 'tile-customApps',
  circles: 'tile-circles',
  manageAppStore: 'tile-manageAppStore',
  manageSurveys: 'tile-manageSurveys',
  surveys: 'tile-surveys',
  transcription: 'tile-transcription',
  ontologies: 'tile-ontologies',
  ontologiesMarketplace: 'tile-ontologiesMarketplace',
  trialMatching: 'tile-trialMatching',
  contentManagement: 'tile-contentManagement',
  ml: 'tile-ml',
  programs: 'tile-programs',
  alerts: 'tile-alerts',
  manageAlerts: 'tile-manageAlerts',
  reseller: 'tile-reseller',
  EMAs: 'tile-EMAs',
  workflows: 'tile-workflows',
  onboardingSnapshot: 'tile-onboardingSnapshot',
  fileValidation: 'tile-fileValidation',
  regenerativeMedicineOps: 'tile-regenerativeMedicineOps',
  reportTemplates: 'tile-reportTemplates',
  zoomAppointments: 'tile-zoomAppointments',
  aiRules: 'tile-aiRules',
  zoriChat: 'tile-zoriChat',
};

const isInCommercialEnv = () => !getIsInFedEnvironment();

const status = {
  active: 'active',
  disabled: 'disabled',
  comingSoon: 'comingSoon',
};

const _safeEnforce = (
  operations: string | string[],
  policy: AbacReducedPolicy,
  attributes?: Record<string, unknown>,
) => {
  const operationEnforcer = attributes
    ? (operation: any) => strictEnforce(operation, policy, attributes)
    : (operation: any) => enforce(operation, policy);

  if (typeof operations === 'string') {
    operations = [operations];
  }
  return policy && !_.isEmpty(policy) && _.some(operations, operationEnforcer);
};

const accountNotDeactivated = (activeAccount: any) =>
  (activeAccount || {}).status !== 'DEACTIVATED';

const isLifeAccount = (activeAccount: any) =>
  idx(activeAccount, _ => _.id) === lifeAccount;

const isFeatureEnabledAndAccountActive = (
  activeAccount: any,
  features: any,
  feature: any,
) =>
  !!activeAccount &&
  ((features || {})[feature] ||
    (activeAccount.features || []).includes(feature));

const tiles = {
  home: {
    id: 'home',
    testId: testIds.home,
    title: 'Home',
    description: 'Home',
    intlId: 'tiles.home',
    uri: urls.app.home(),
    tileIcon: Home,
    requiresProject: false,
    shouldDisplay: () => true,
  },
  accountInformation: {
    id: 'account-information',
    testId: testIds.accountInformation,
    title: 'Account Info',
    description: '',
    intlId: 'home.primary.accountInfo',
    uri: urls.app.roots.account(),
    tileIcon: Settings,
    status: status.active,
    requiresProject: false,
    shouldDisplay: () => true,
  },
  manageProjects: {
    id: 'projects',
    testId: testIds.manageProjects,
    title: 'Projects',
    description: '',
    intlId: 'tiles.manageProjects',
    uri: urls.app.projects(),
    tileIcon: Box,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any) =>
      !!activeAccount && accountNotDeactivated(activeAccount),
  },
  invitations: {
    id: 'invitations',
    testId: testIds.invitations,
    title: 'Invitations',
    description: '',
    intlId: 'manageUsers.primary.invitations',
    uri: urls.app.invitations(),
    tileIcon: Mail,
    status: status.active,
    requiresProject: false,
    shouldDisplay: () => true,
  },
  manageCohorts: {
    id: 'cohorts',
    testId: testIds.manageCohorts,
    title: 'Cohorts',
    description: '',
    intlId: 'tiles.manageCohorts',
    uri: urls.app.cohorts(),
    tileIcon: Users,
    navIcon: Users,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'cohorts'),
  },
  manageKnowledge: {
    id: 'manage-knowledge',
    testId: testIds.manageKnowledge,
    title: 'Manage Knowledge',
    description: '',
    intlId: 'tiles.manageKnowledge',
    uri: '',
    tileIcon: Lightbulb,
    status: status.disabled,
    requiresProject: true,
    shouldDisplay: (activeAccount: any) =>
      isInCommercialEnv() &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  manageProjectKnowledge: {
    id: 'manage-project-knowledge',
    testId: testIds.manageProjectKnowledge,
    title: 'Knowledge',
    description: '',
    intlId: 'tiles.project.manageKnowledge',
    uri: urls.app.projectKnowledge(),
    tileIcon: Lightbulb,
    navIcon: Lightbulb,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      isInCommercialEnv() &&
      _safeEnforce(
        [
          permissions.readData,
          permissions.projectAdmin,
          permissions.readMaskedData,
        ],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  billing: {
    id: 'billing',
    testId: testIds.billing,
    title: 'Billing',
    description: '',
    intlId: 'tiles.billing',
    uri: urls.app.billing(),
    tileIcon: DollarSign,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount && _safeEnforce(permissions.billingAdmin, policy),
  },
  manageMetadata: {
    id: 'manage-metadata',
    testId: testIds.manageMetadata,
    title: 'Manage Metadata',
    description: '',
    intlId: 'tiles.manageMetadata',
    uri: '',
    tileIcon: Database,
    status: status.disabled,
    requiresProject: true,
    shouldDisplay: (activeAccount: any) =>
      !!activeAccount && accountNotDeactivated(activeAccount),
  },
  usage: {
    id: 'usage',
    testId: testIds.usage,
    title: 'Usage',
    description: '',
    intlId: 'tiles.usage',
    uri: urls.app.usage(),
    tileIcon: Sliders,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.billingAdmin, policy),
  },
  apiKeys: {
    id: 'api-keys',
    testId: testIds.apiKeys,
    title: 'API Keys',
    description: '',
    intlId: 'accountInfo.primary.apiKeys',
    uri: urls.app.apiKeys(),
    tileIcon: Lock,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.apiKeyUser, policy),
  },
  authClients: {
    id: 'auth-clients',
    testId: testIds.authClients,
    title: 'Auth Client',
    description: '',
    intlId: 'accountInfo.primary.authClients',
    uri: urls.app.clients(),
    tileIcon: Login,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      activeAccount.type === 'ENTERPRISE' &&
      activeAccount.products?.includes('AuthClient') &&
      _safeEnforce(permissions.accountAdmin, policy),
  },
  manageAccount: {
    id: 'manage-account',
    testId: testIds.manageAccounts,
    title: 'Manage Account',
    description: '',
    intlId: 'accountInfo.primary.manageAccount',
    uri: urls.app.manageAccount(),
    tileIcon: Settings,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.accountAdmin, policy),
  },
  createAccount: {
    id: 'create-account',
    testId: testIds.createAccount,
    title: 'Create Account',
    description: '',
    intlId: 'accountInfo.primary.createAccount',
    uri: urls.app.createAccount(),
    tileIcon: PlusSquare,
    status: status.active,
    requiresProject: false,
    shouldDisplay: () => true,
  },
  accountAuditing: {
    id: 'account-auditing',
    testId: testIds.accountAuditing,
    description: '',
    title: 'Account Auditing',
    intlId: 'accountInfo.primary.accountAuditing',
    uri: urls.app.accountAuditing(),
    tileIcon: Archive,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      (features || {}).accountAuditing &&
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.accountAdmin, policy),
  },
  manageAccountUsers: {
    id: 'manage-account-users',
    testId: testIds.manageAccountUsers,
    title: 'Users',
    description: '',
    intlId: 'accountInfo.primary.manageAccountUsers',
    uri: urls.app.manageAccountUsers(),
    tileIcon: Users,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.accessAdmin, policy),
  },
  groups: {
    id: 'groups',
    testId: testIds.groups,
    title: 'Groups',
    description: '',
    intlId: 'manageUsers.primary.groups',
    uri: urls.app.groups(),
    tileIcon: Users,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.inviteUsers, permissions.accessAdmin], policy),
  },
  accessControl: {
    id: 'access-control',
    testId: testIds.accessControl,
    title: 'Access Control',
    description: '',
    intlId: 'manageUsers.primary.accessControl',
    uri: urls.app.accessControl(),
    tileIcon: Lock,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.accessAdmin, policy),
  },
  projects: {
    id: 'project-info',
    testId: testIds.projects,
    title: 'Project Info',
    description: '',
    intlId: 'manageProjects.primary.projectInfo',
    uri: '',
    tileIcon: Info,
    status: status.disabled,
    requiresProject: false,
    shouldDisplay: () => false,
  },
  addData: {
    id: 'add-data',
    testId: testIds.addData,
    title: 'Add Data',
    description: '',
    intlId: 'manageProjects.primary.addData',
    uri: urls.app.addData(),
    navIcon: PlusCircle,
    tileIcon: PlusCircle,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.createData, policy),
  },
  subjectSearch: {
    id: 'subjectsSearch',
    testId: testIds.subjectSearch,
    title: 'Subjects',
    description: '',
    intlId: 'manageProjects.primary.subjectSearch',
    uri: urls.app.patients(),
    tileIcon: User,
    navIcon: Subjects,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.readMaskedData, permissions.readData], policy),
  },
  files: {
    id: 'files',
    testId: testIds.files,
    title: 'Files',
    description: '',
    intlId: 'manageProjects.primary.files',
    uri: urls.app.files(),
    tileIcon: Briefcase,
    navIcon: Briefcase,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(
        [
          permissions.downloadFile,
          permissions.createData,
          permissions.readData,
          permissions.fileAdmin,
        ],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'files'),
  },
  sftp: {
    id: 'sftp',
    testId: testIds.sftp,
    title: 'SFTP',
    description: '',
    intlId: 'manageProjects.primary.sftp',
    uri: urls.app.sftp(),
    tileIcon: File,
    navIcon: File,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      isInCommercialEnv() &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.projectAdmin, permissions.fileAdmin], policy),
  },
  fileTransfer: {
    id: 'fileTransfer',
    testId: testIds.fileTransfer,
    title: 'Transfer',
    description: '',
    intlId: 'manageProjects.primary.filesTransfer',
    uri: urls.app.fileTransfer(),
    tileIcon: File,
    navIcon: File,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      isInCommercialEnv() &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.projectAdmin, permissions.fileAdmin], policy),
  },
  insights: {
    id: 'insights',
    testId: testIds.insights,
    title: 'Insights',
    description: '',
    intlId: 'manageProjects.primary.insights',
    uri: urls.app.insights(),
    tileIcon: PieChart,
    navIcon: PieChart,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      isInCommercialEnv() &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'insights'),
  },
  genomics: {
    id: 'omics',
    testId: testIds.genomics,
    title: 'Omics',
    description: '',
    intlId: 'manageProjects.primary.genomics',
    uri: urls.app.omics.dashboard(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      isInCommercialEnv() &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  genomicsTests: {
    id: 'omicsTests',
    testId: testIds.genomicsTests,
    title: 'Tests',
    description: '',
    intlId: 'manageProjects.primary.genomicTests',
    uri: urls.app.omics.tests(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      isInCommercialEnv() &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  genomicsIngestions: {
    id: 'omicsIngestions',
    testId: testIds.genomicsIngestions,
    title: 'Ingestions',
    description: '',
    intlId: 'manageProjects.primary.genomicIngestions',
    uri: urls.app.omics.ingestions(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      isInCommercialEnv() &&
      _safeEnforce(permissions.createGenomicData, policy) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  genomicsActions: {
    id: 'omicsActions',
    testId: testIds.genomicsActions,
    title: 'Actions',
    description: '',
    intlId: 'manageProjects.primary.genomicsActions',
    uri: urls.app.omics.actions(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.fileAdmin], policy) &&
      _safeEnforce([permissions.createGenomicData], policy),
  },
  tasks: {
    id: 'tasks',
    testId: testIds.tasks,
    title: 'Tasks',
    description: '',
    intlId: 'manageProjects.primary.tasks',
    uri: urls.app.tasks(),
    tileIcon: List,
    navIcon: List,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      !!activeAccount &&
      features &&
      isInCommercialEnv() &&
      accountNotDeactivated(activeAccount) &&
      activeAccount.type === 'ENTERPRISE' &&
      !getIsAccountFeatureDisabled(activeAccount, 'automation') &&
      _safeEnforce(
        [
          permissions.readData,
          permissions.readMaskedData,
          permissions.workflowAdmin,
        ],
        policy,
      ),
  },
  educationContent: {
    id: 'education-content',
    testId: testIds.educationContent,
    title: 'Education Content',
    intlId: 'home.secondary.educationContent',
    uri: urls.app.educationContent(),
    tileIcon: BookOpen,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      isInCommercialEnv() &&
      _safeEnforce('eduContentAdmin', policy),
  },
  manageEHRs: {
    title: 'EHRs',
    testId: testIds.manageEHRs,
    intlId: 'accountInfo.secondary.manageEHRs',
    uri: urls.app.ehrs(),
    tileIcon: List,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      (_safeEnforce('ehrAdmin', policy) ||
        _safeEnforce('accountAdmin', policy)),
  },
  features: {
    title: 'Features',
    testId: testIds.features,
    intlId: 'accountInfo.secondary.features',
    uri: urls.app.features(),
    tileIcon: List,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount && _safeEnforce('accountAdmin', policy),
  },
  manageProjectConsent: {
    id: 'manageProjectConsent',
    testId: testIds.manageProjectConsent,
    title: 'Consent',
    intlId: 'accountInfo.secondary.manageProjectConsent',
    uri: urls.app.consentManagement.project(),
    tileIcon: CheckCircle,
    navIcon: CheckCircle,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(activeAccount, features, 'consent') &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'consent'),
  },
  consent: {
    id: 'consent',
    testId: testIds.consent,
    title: 'My Consent',
    description: '',
    intlId: 'tiles.consent',
    uri: urls.app.consent(),
    icon: CheckCircle,
    tileIcon: CheckCircle,
    navIcon: CheckCircle,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(activeAccount, features, 'consent') &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      _safeEnforce([permissions.updateData], policy),
  },
  userGuide: {
    id: 'user-guide',
    testId: testIds.userGuide,
    title: 'Help',
    description: '',
    intlId: 'home.primary.userGuide',
    uri: {
      external: true,
      url: 'https://platform.docs.lifeomic.com/user-guides/',
    },
    navIcon: BookOpen,
    status: status.active,
    requiresProject: false,
    shouldDisplay: () => true,
  },
  customApps: {
    id: 'custom-apps',
    testId: testIds.customApps,
    title: 'Custom Apps',
    description: '',
    intlId: 'home.primary.customApps',
    uri: urls.app.customApps(),
    navIcon: Package,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'customApps'),
  },
  circles: {
    id: 'circles',
    testId: testIds.circles,
    title: 'Circles',
    description: '',
    intlId: 'tiles.circles',
    uri: urls.app.circles(),
    tileIcon: Circles,
    navIcon: Circles,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) => {
      if (!activeAccount) {
        return false;
      }
      return (
        (isLifeAccount(activeAccount) ||
          _safeEnforce(permissions.accessAdmin, policy)) &&
        !getIsAccountFeatureDisabled(activeAccount, 'circles')
      );
    },
  },
  manageAppStore: {
    id: 'manage-app-store',
    testId: testIds.manageAppStore,
    title: 'Manage Marketplace',
    description: '',
    intlId: 'tiles.manageAppStore',
    uri: urls.appStoreAdmin(),
    tileIcon: MarketplaceIcon,
    status: status.active,
    isExternal: true,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any, _features: any) => {
      const abacAttributes = {
        resource: {
          type: 'marketplaceApp',
        },
      };
      // this may eventually be opened up to other accounts
      return (
        isLifeAccount(activeAccount) &&
        _safeEnforce(
          [permissions.createData, permissions.deleteData],
          policy,
          abacAttributes,
        )
      );
    },
  },
  manageSurveys: {
    id: 'manage-surveys',
    testId: testIds.manageSurveys,
    title: 'Surveys',
    description: '',
    intlId: 'tiles.manageSurveys',
    tileIcon: CheckList,
    uri: pwtUrls.app.manageSurveys(),
    navIcon: CheckList,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(activeAccount, features, 'surveys') &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'surveys'),
  },
  surveys: {
    id: 'surveys',
    testId: testIds.surveys,
    title: 'My Surveys',
    description: '',
    intlId: 'tiles.surveys',
    uri: urls.app.surveys(),
    tileIcon: CheckList,
    navIcon: CheckList,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(activeAccount, features, 'surveys') &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      _safeEnforce(permissions.updateData, policy),
  },
  ontologies: {
    id: 'ontologies',
    testId: testIds.ontologiesMarketplace,
    title: 'Ontologies',
    description: '',
    intlId: 'manageProjects.primary.ontologies',
    uri: urls.app.ontologies.local(),
    tileIcon: NodeMesh,
    navIcon: NodeMesh,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.readData, permissions.readMaskedData], policy),
  },
  ontologiesMarketplace: {
    id: 'ontologiesMarketplace',
    testId: testIds.ontologiesMarketplace,
    title: 'Marketplace',
    description: '',
    intlId: 'manageProjects.primary.ontologiesMarketplace',
    uri: urls.app.ontologies.marketplace(),
    tileIcon: NodeMesh,
    navIcon: NodeMesh,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.readData, permissions.readMaskedData], policy),
  },
  trialMatching: {
    id: 'trialMatching',
    testId: testIds.trialMatching,
    title: 'Clinical Trials',
    description: '',
    intlId: 'manageProjects.primary.trialMatching',
    uri: urls.app.trialMatching.root(),
    tileIcon: CapsulePills,
    navIcon: CapsulePills,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) => {
      return (
        _safeEnforce([permissions.readData], policy) &&
        !getIsAccountFeatureDisabled(activeAccount, 'trialMatching')
      );
    },
  },
  ml: {
    id: 'ml',
    testId: testIds.ml,
    title: 'Machine Learning',
    description: '',
    intlId: 'manageProjects.primary.ml',
    uri: urls.app.ml.root(),
    tileIcon: Cpu,
    navIcon: Cpu,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      _safeEnforce([permissions.readData], policy) &&
      activeAccount.products?.includes('PatientML'),
  },
  contentManagement: {
    id: 'contentManagement',
    testId: testIds.contentManagement,
    title: 'Content Management',
    description: '',
    intlId: 'manageProjects.primary.contentManagement',
    uri: urls.app.contentManagement.root(),
    tileIcon: Library,
    navIcon: Library,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, _policy: any, features: any) =>
      features?.contentManagement &&
      !!activeAccount &&
      activeAccount.products?.includes('ContentManagement'),
  },
  reseller: {
    id: 'reseller',
    testId: testIds.reseller,
    title: 'Reseller',
    description: 'Reseller',
    intlId: 'tiles.reseller',
    uri: urls.app.reseller(),
    tileIcon: Settings,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.createData, policy) &&
      activeAccount.products?.includes('Reseller'),
  },
  EMAs: {
    id: 'EMAs',
    testId: testIds.EMAs,
    title: 'Ecological Momentary Assessments',
    description: '',
    intlId: 'manageProjects.primary.EMAs',
    uri: urls.app.EMAs.root(),
    // Icons still TBD
    tileIcon: CheckList,
    navIcon: CheckList,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      features?.ecologicalMomentaryAssessments &&
      !!activeAccount &&
      _safeEnforce([permissions.readData], policy),
  },
  workflows: {
    id: 'workflows',
    testId: testIds.workflows,
    title: 'Workflows',
    intlId: 'tiles.workflows',
    // For now, there is only one workflow, so we can use the onboarding journey URL
    uri: urls.app.workflows.onboardingSnapshot(),
    tileIcon: Procedure,
    navIcon: Procedure,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: { id?: string }) =>
      !!activeAccount && activeAccount.id?.startsWith('fountainlife'),
  },
  onboardingSnapshot: {
    id: 'onboardingSnapshot',
    testId: testIds.onboardingSnapshot,
    title: 'Onboarding Snapshot',
    description: '',
    intlId: 'tiles.workflows',
    uri: urls.app.workflows.onboardingSnapshot(),
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, _policy: any, _features: any) =>
      !!activeAccount && activeAccount.id?.startsWith('fountainlife'),
  },
  fileValidation: {
    id: 'fileValidation',
    testId: testIds.fileValidation,
    title: 'File Validation',
    description: '',
    intlId: 'tiles.fileValidation',
    uri: urls.app.workflows.fileValidation(),
    tileIcon: File,
    navIcon: File,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, _policy: any, _features: any) =>
      !!activeAccount && activeAccount.id?.startsWith('fountainlife'),
  },
  regenerativeMedicineOps: {
    id: 'regenerativeMedicineOps',
    testId: testIds.regenerativeMedicineOps,
    title: 'Regenerative Medicine Ops',
    description: '',
    intlId: 'tiles.regenerativeMedicineOps',
    uri: urls.app.workflows.regenerativeMedicineOps(),
    tileIcon: SparkleHeart,
    navIcon: SparkleHeart,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, _policy: any, features: any) =>
      !!activeAccount &&
      activeAccount.id?.startsWith('fountainlife') &&
      features?.regenerativeMedicineOps,
  },
  reportTemplates: {
    id: 'reportTemplates',
    testId: testIds.reportTemplates,
    title: 'Report Templates',
    description: '',
    intlId: 'tiles.reportTemplates',
    uri: urls.app.workflows.reportTemplates(),
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any) =>
      !!activeAccount && activeAccount.id?.startsWith('fountainlife'),
  },
  zoomAppointments: {
    id: 'zoomAppointments',
    testId: testIds.zoomAppointments,
    title: 'Zoom Appointments',
    description: '',
    intlId: 'tiles.zoomAppointments',
    uri: urls.app.workflows.zoomAppointments(),
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any) =>
      ['fountainlife', 'fountainlifetest'].includes(activeAccount?.id),
  },
  aiRules: {
    id: 'aiRules',
    testId: testIds.aiRules,
    title: 'Zori AI',
    description: '',
    intlId: 'tiles.zoriRules',
    uri: urls.app.workflows.zoriRules(),
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any) =>
      ['fountainlife', 'fountainlifetest'].includes(activeAccount?.id),
  },
  zoriChat: {
    id: 'zoriChat',
    testId: testIds.zoriChat,
    title: 'Zori Chat',
    description: '',
    intlId: 'tiles.zoriChat',
    uri: urls.app.workflows.zoriChat(),
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, _policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(
        activeAccount,
        features,
        'clinicianAgentChat',
      ) ||
      isFeatureEnabledAndAccountActive(
        activeAccount,
        features,
        'clinicianAgentChatGlobal',
      ),
  },
};

const tileConfig = {
  home: {
    tile: tiles.home,
    primary: [
      { tile: tiles.accountInformation },
      { tile: tiles.billing },
      { tile: tiles.usage },
      { tile: tiles.circles },
      { tile: tiles.educationContent },
      { tile: tiles.manageAppStore },
      { tile: tiles.trialMatching },
      { tile: tiles.workflows },
    ],
  },
  accountInfo: {
    tile: tiles.accountInformation,
    primary: [
      { tile: tiles.usage },
      { tile: tiles.apiKeys },
      { tile: tiles.billing },
      { tile: tiles.authClients },
      { tile: tiles.manageAccount },
      { tile: tiles.features },
      { tile: tiles.createAccount },
      { tile: tiles.manageAccountUsers },
      { tile: tiles.accountAuditing },
    ],
    secondary: [{ tile: tiles.educationContent }, { tile: tiles.manageEHRs }],
  },
  manageProjects: {
    tile: tiles.manageProjects,
    primary: [
      { tile: tiles.projects },
      { tile: tiles.insights },
      { tile: tiles.subjectSearch },
      { tile: tiles.manageCohorts },
      { tile: tiles.tasks },
      { tile: tiles.files },
      { tile: tiles.manageProjectKnowledge },
      { tile: tiles.addData },
      { tile: tiles.genomics },
      { tile: tiles.manageProjectConsent },
      { tile: tiles.manageSurveys },
      { tile: tiles.EMAs },
      { tile: tiles.workflows },
    ],
    secondary: [],
  },
};

export { tiles };
export default tileConfig;
